import { Component, type ReactNode, useCallback, useState } from 'react';
import { ShieldAlert, RotateCcw } from 'lucide-react';
import { cn } from './cn';

// ── ErrorCard ──────────────────────────────────────────────────────────────

export interface ErrorCardProps {
  title?: string;
  message?: string;
  onRetry?: () => void;
  className?: string;
}

export function ErrorCard({
  title = 'Something went wrong',
  message,
  onRetry,
  className,
}: ErrorCardProps) {
  return (
    <div
      className={cn(
        'flex flex-col items-center gap-4 rounded-lg border border-state-danger/25 bg-state-danger/5 px-6 py-10 text-center',
        className,
      )}
    >
      <div className="flex h-10 w-10 items-center justify-center rounded-full bg-state-danger/10 text-state-danger">
        <ShieldAlert className="h-5 w-5" />
      </div>
      <div className="space-y-1">
        <p className="text-sm font-semibold text-text-primary">{title}</p>
        {message && (
          <p className="font-mono text-[11px] text-text-tertiary break-all max-w-xs">
            {message}
          </p>
        )}
      </div>
      {onRetry && (
        <button
          onClick={onRetry}
          className="inline-flex items-center gap-1.5 rounded-md border border-border-default bg-surface-1 px-3 py-1.5 text-xs font-medium text-text-primary transition-colors hover:bg-surface-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-focus"
        >
          <RotateCcw className="h-3 w-3" />
          Retry
        </button>
      )}
    </div>
  );
}

// ── ErrorBoundary ──────────────────────────────────────────────────────────

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: (error: Error, reset: () => void) => ReactNode;
}

interface ErrorBoundaryState {
  error: Error | null;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  componentDidCatch(error: Error, info: { componentStack: string }) {
    console.error('[ErrorBoundary] Caught render error:', error, info.componentStack);
  }

  reset = () => {
    this.setState({ error: null });
  };

  render() {
    const { error } = this.state;
    const { children, fallback } = this.props;

    if (error) {
      if (fallback) {
        return fallback(error, this.reset);
      }
      return (
        <div className="p-6">
          <ErrorCard
            title="Something went wrong"
            message={error.message}
            onRetry={this.reset}
          />
        </div>
      );
    }

    return children;
  }
}

// ── useErrorHandler ────────────────────────────────────────────────────────

/**
 * Hook that imperatively throws an error into the nearest ErrorBoundary.
 * Useful for async errors that happen outside of render (e.g., event handlers).
 *
 * Usage:
 *   const throwError = useErrorHandler();
 *   throwError(new Error('oops'));  // bubbles to nearest ErrorBoundary
 */
export function useErrorHandler(): (error: Error) => void {
  const [, setState] = useState<Error | null>(null);

  return useCallback((error: Error) => {
    // Setting state inside setState re-throw trick propagates to boundary
    setState(() => {
      throw error;
    });
  }, []);
}
